import { CloseIcon, CopyIcon } from '@chakra-ui/icons'
import {
  Box, Breadcrumb, BreadcrumbItem, BreadcrumbLink, Divider,
  Flex,
  Heading, Icon,
  IconButton,
  Link,
  Spacer,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text as ChakraText,
  Th,
  Thead,
  Tooltip,
  Tr,
  useClipboard
} from '@chakra-ui/react'
import moment from 'moment'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa'
import { IoArrowBackCircle } from 'react-icons/io5'
import { useHistory } from 'react-router-dom'
import type { CellProps, Column } from 'react-table'
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable
} from 'react-table'
import useFetch, { CachePolicies } from 'use-http'
import { BaseLayout } from '..'
import { colors } from '../../../ui-kit'
import { FeaturesEnum } from '../../../utils/features'
import CustomPlanCTA from '../../components/CustomPlan'
import Hover from '../../components/Hover'
import Pagination from '../../components/Pagination'
import SearchBar from '../../components/SearchBar'
import useToast from '../../hooks/useToast'
import type { DownloadJobs } from '../../interfaces'
import { UserContext } from '../../providers'
import { PublicLinksInfoContext } from '../../providers/PublicLinksInfoProvider'
import { MainContentHeadingBox, MainContentWrapper } from '../Base/MainContent'

const DownloadsLayout = (): React.ReactElement => {
  const { user } = useContext(UserContext)
  const { t } = useTranslation()
  const toast = useToast()
  const { isPublicLink, urlPrefix } = useContext(PublicLinksInfoContext)
  const [data, setData] = useState<DownloadJobs[]>()
  const [copyLink, setCopyLink] = useState<string>('')

  const { request, loading } = useFetch<DownloadJobs[]>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const history = useHistory()

  const getData = async (): Promise<void> => {
    if (
      isPublicLink ||
      user?.pricingPlan.featuresList.includes(
        FeaturesEnum.MEDIA_LIBRARY_STORAGE,
      )
    ) {
      const response = await request.get(`${urlPrefix}api/v1/download`)
      if (request.error) {
        toast({
          description: t('error_fetching_data', 'Error in fetching data. Try refreshing the page.'),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else {
        setData(response)
      }
    }
  }

  useEffect(() => {
    void getData()
  }, [user, urlPrefix, isPublicLink])
  useEffect(() => {
    if (copyLink) {
      onCopy()
      toast({
        description: t('copied_url_to_clipboard', 'Copied URL to clipboard'),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    }
  }, [copyLink])
  const { onCopy } = useClipboard(copyLink)
  const tableData = useMemo<DownloadJobs[]>(() => data ?? [], [data])

  const columns = useMemo<Array<Column<DownloadJobs>>>(
    () => {
      const _columns = [
        {
          Header: t('name', 'Name').toString(),
          accessor: values => values.name,
          Cell: ({ value }: CellProps<DownloadJobs, string | undefined>) => (
            <Box maxW="25em">
              <Tooltip label={value}>
                <ChakraText isTruncated>{value}</ChakraText>
              </Tooltip>
            </Box>
          ),
        },
        {
          Header: t('file_count', 'File count').toString(),
          accessor: values => values.fileCount,
        },
        {
          Header: t('folder_count', 'Folder count').toString(),
          accessor: values => values.folderCount,
        },
        {
          Header: t('time', 'Time').toString(),
          accessor: values => values.updatedAt,
          Cell: ({ value }: CellProps<DownloadJobs, string | undefined>) => (
            <Tooltip label={moment(value).format('ddd, MMMM Do YYYY, h:mm:ss A')}>
              <ChakraText>{moment(value).fromNow()}</ChakraText>
            </Tooltip>
          ),
        },
        {
          Header: t('download', 'Download').toString(),
          accessor: values => values.storageURL,
          Cell: ({ row, value }: CellProps<DownloadJobs, string | undefined>) => {
            if (
              row.original.status === 'Partially succeeded' ||
              row.original.status === 'Succeeded'
            ) {
              return (
                <Hover>
                  {isHover => (
                    <Box minWidth="100px">
                      <Link href={value} download>
                        {t('download', 'Download')}
                      </Link>
                      {isHover ? (
                        <CopyIcon
                          cursor="pointer"
                          marginLeft="10px"
                          onClick={() => {
                            void setCopyLink(String(value))
                          }}
                        />
                      ) : null}
                    </Box>
                  )}
                </Hover>
              )
            } else if (row.original.status === 'Pending') {
              return <Box minWidth="100px">{t('processing', 'Processing')}</Box>
            } else if (row.original.status === 'Failed') {
              return <Box minWidth="100px">{t('failed', 'Failed')}</Box>
            }
            return <Box minWidth="100px">NA</Box>
          },
        },
      ]

      if (
        isPublicLink !== true &&
        user?.pricingPlan.featuresList.includes(FeaturesEnum.PUBLIC_LINKS)
      ) {
        _columns.push({
          Header: t('via_public_link', 'Via public link').toString(),
          accessor: values => values.publicLinkId,
          Cell: ({ value }: CellProps<DownloadJobs, string | undefined>) => {
            if (value) {
              return (
                <Tooltip
                  label={t(
                    'via_public_link_explanation',
                    'This download was triggered via a public link',
                  )}
                >
                  <ChakraText cursor={'pointer'}>{t('yes', 'Yes')}</ChakraText>
                </Tooltip>
              )
            } else {
              return <ChakraText>{t('no', 'No')}</ChakraText>
            }
          },
        })
      }

      return _columns
    },
    [isPublicLink],
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    state: { pageIndex, pageSize, ...tableState },
    canPreviousPage,
    canNextPage,
    pageCount,
    pageOptions,
    setGlobalFilter,
    setPageSize,
    previousPage,
    nextPage,
    gotoPage,
  } = useTable<DownloadJobs>(
    {
      columns,
      data: tableData,
      initialState: {
        sortBy: [
          {
            id: 'Time',
            desc: true,
          },
        ],
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
  )

  const pageOffset = pageIndex * pageSize

  return (
    <BaseLayout>
      <MainContentHeadingBox>
        <Stack direction={['column']} spacing={1}>
          <Heading
            as="h1"
            size="md"
            fontWeight="semibold"
            color="appDarkIndigo"
          >
            {t('downloads', 'Downloads')}
          </Heading>
          {!isPublicLink ? (
            <ChakraText>
              {t(
                'downloads_page_subheading',
                'ZIP file downloads you have requested in the past 7 days',
              )}
            </ChakraText>
          ) : null}
        </Stack>
      </MainContentHeadingBox>
      <MainContentWrapper style={{ gap: '0.5em' }}>
        {
        isPublicLink ||
        user?.pricingPlan.featuresList.includes(
          FeaturesEnum.MEDIA_LIBRARY_STORAGE,
        ) ? (
          <>
            <Breadcrumb
              onClick={() => history.goBack()}
              cursor={"pointer"}
            >
              <BreadcrumbItem>
                <Icon
                  marginTop={1}
                  as={IoArrowBackCircle}
                  boxSize={5}
                  color="appSlate"
                  mr={1}
                  mb={1}
                />
                <Tooltip label={t('go_back', 'Go back')}>
                  <BreadcrumbLink
                    as={Link}
                    color="appSlate"
                    fontWeight="medium"
                    maxW="8em"
                    isTruncated
                    borderRadius="lg"
                    paddingX={1}
                    _focus={{
                      outline: 'none',
                    }}
                  >
                    {t('go_back', 'Go back')}
                  </BreadcrumbLink>
                </Tooltip>
              </BreadcrumbItem>
            </Breadcrumb>
            <Flex align="center" paddingBottom="0.5em">
              <SearchBar
                searchQuery={tableState.globalFilter as string}
                setSearchQuery={setGlobalFilter}
                minWidth="30em"
              />
              <Spacer />
              {rows.length ? (
                <ChakraText fontWeight="500">
                  {t('showing', 'Showing')}{' '}
                  {pageOffset + (pageCount === 0 ? 0 : 1)} {t('to', 'to')}{' '}
                  {pageOffset + pageSize <= rows.length
                    ? pageOffset + pageSize
                    : rows.length}{' '}
                  {t('of', 'of')} {rows.length} {t('entries', 'entries')}
                </ChakraText>
              ) : null}
            </Flex>
            <Table {...getTableProps}>
              <Thead bgColor="appPaleGreyTwo">
                {headerGroups.map(headerGroup => {
                  const { key, ...restHeaderGroupProps } =
                    headerGroup.getHeaderGroupProps()
                  return (
                    <Tr key={key} {...restHeaderGroupProps}>
                      {headerGroup.headers.map(column => {
                        const { key: keyCol, ...restColumn } =
                          column.getHeaderProps(column.getSortByToggleProps())
                        return (
                          <Th key={keyCol} {...restColumn}>
                            {column.render('Header')}
                            {!column.disableSortBy ? (
                              <Box
                                as="span"
                                pl="2"
                                color={colors.appBattleshipGrey}
                              >
                                {column.isSorted ? (
                                  column.isSortedDesc ? (
                                    <Icon
                                      as={FaSortUp}
                                      aria-label={t(
                                        'sorted_descending',
                                        'sorted descending',
                                      )}
                                    />
                                  ) : (
                                    <Icon
                                      as={FaSortDown}
                                      aria-label={t(
                                        'sorted_ascending',
                                        'sorted ascending',
                                      )}
                                    />
                                  )
                                ) : (
                                  <Icon
                                    as={FaSort}
                                    aria-label={t(
                                      'sort_by_column',
                                      'sort by column',
                                    )}
                                  />
                                )}
                              </Box>
                            ) : null}
                          </Th>
                        )
                      })}
                    </Tr>
                  )
                })}
              </Thead>
              <Tbody {...getTableBodyProps()}>
                {!loading ? (
                  rows.length ? (
                    page.map(row => {
                      prepareRow(row)
                      const { key, ...restRowProps } = row.getRowProps()
                      return (
                        <Tr key={key} {...restRowProps}>
                          {row.cells.map(cell => {
                            const { key: keyRow, ...restCellProps } =
                              cell.getCellProps()
                            return (
                              <Td key={keyRow} {...restCellProps}>
                                <ChakraText>{cell.render('Cell')}</ChakraText>
                              </Td>
                            )
                          })}
                        </Tr>
                      )
                    })
                  ) : (
                    <Tr>
                      <Td colSpan={6} textAlign="center">
                        <ChakraText>
                          {t(
                            'no_downloads_available',
                            'No downloads available',
                          )}
                        </ChakraText>
                      </Td>
                    </Tr>
                  )
                ) : (
                  <Tr>
                    <Td colSpan={6} textAlign="center">
                      <Spinner
                        thickness="4px"
                        speed="0.65s"
                        emptyColor="appPaleGrey"
                        color="appBlue"
                        size="xl"
                      />
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
            {pageOptions.length > 0 ? (
              <Divider mt={4} mb={4} />
            ) : (
              <Box mb={4} />
            )}
            <Flex>
              <Spacer />
              <Pagination
                pageIndex={pageIndex}
                pageSize={pageSize}
                canPreviousPage={canPreviousPage}
                canNextPage={canNextPage}
                pageCount={pageCount}
                pageOptions={pageOptions}
                setPageSize={setPageSize}
                previousPage={previousPage}
                nextPage={nextPage}
                gotoPage={gotoPage}
              />
            </Flex>
          </>
        ) : (
          <CustomPlanCTA feature={FeaturesEnum.MEDIA_LIBRARY_STORAGE} />
        )}
      </MainContentWrapper>
    </BaseLayout>
  )
}

export default DownloadsLayout
